/**
 * Team play in the setup page and the lobby (docs/setup-redesign/teams-config.md):
 * the copy for a team format, the options the Team size and Players controls
 * offer on a map, snapping when the map or size changes, and the seat moves a
 * lobby edit causes. Pure, so the page, the lobby and the tests share one
 * definition. The format rule itself is `validateTeamFormat` / `teamFormats`
 * in @warcarp/api-types, which the server applies too.
 */
import {
  TEAM_IDS,
  TEAM_SIZES,
  mapSeatRange,
  snapTeamSeats,
  teamForSlot,
  teamFormats,
  teamLabel,
  type GameConfig,
  type GamePlayerResponse,
  type MapDef,
  type TeamId,
  type TeamSize,
  type TeamsConfig,
} from '@warcarp/api-types'

type SeatMap = Pick<MapDef, 'territories' | 'fixedSetup'>

/** The locked auto-start's hint: a team game starts when every seat is filled. */
export const TEAM_AUTO_START_HINT = 'on: team games always auto-start when full'
/** Desktop/tablet: the same, as a line under the locked switch (the label line is one line). */
export const TEAM_AUTO_START_NOTE = 'On: team games always auto-start when full.'

/** "3v3", "2v2v2". */
export function teamFormatName(size: number, players: number): string {
  const teams = Math.max(1, Math.round(players / size))
  return Array.from({ length: teams }, () => String(size)).join('v')
}

/** "Team A, Team B" for two teams; "Team A, B, C" for more. */
export function teamListLabel(teams: number): string {
  const ids = TEAM_IDS.slice(0, Math.max(0, teams))
  if (ids.length <= 2) return ids.map(teamLabel).join(', ')
  return `Team ${ids.join(', ')}`
}

const assignmentShort = (teams: TeamsConfig) => (teams.assignment === 'manual' ? 'pick seats' : 'random teams')

/** The setup result line: "3v3 · Team A, Team B · 6 seats · pick seats". */
export function teamsResultLine(teams: TeamsConfig, players: number): string {
  return `${teamFormatName(teams.size, players)} · ${teamListLabel(players / teams.size)} · ${players} seats · ${assignmentShort(teams)}`
}

/** Summaries and the rules card: "3v3 · pick seats". */
export function teamsShortLabel(teams: TeamsConfig, players: number): string {
  return `${teamFormatName(teams.size, players)} · ${assignmentShort(teams)}`
}

/** Lobby and game list copy: "Random teams of 3" / "Teams of 2 · pick your seat". */
export function teamsLobbyLabel(teams: TeamsConfig): string {
  return teams.assignment === 'manual' ? `Teams of ${teams.size} · pick your seat` : `Random teams of ${teams.size}`
}

/** The rules table's Teams value: "Teams of 3 · pick seats". */
export function teamsRuleValue(teams: TeamsConfig): string {
  return `Teams of ${teams.size} · ${teams.assignment === 'manual' ? 'pick seats' : 'random'}`
}

function seatsPhrase(map: SeatMap | null | undefined): string {
  const range = mapSeatRange(map)
  return range.min === range.max ? `${range.max}` : `${range.min}–${range.max}`
}

export interface TeamSizeOption {
  size: TeamSize
  disabled: boolean
  /** Why the size is disabled ("This map seats 4 players, so only teams of 2 fit."). */
  reason: string | null
}

/** The Team size control: 2 · 3 · 4, a size with no valid format on the map disabled with the reason. */
export function teamSizeOptions(map: SeatMap | null | undefined, mapName = 'This map'): TeamSizeOption[] {
  const formats = teamFormats(map)
  const fitting = TEAM_SIZES.filter((size) => formats.some((f) => f.size === size))
  const fit = fitting.length === 0 ? 'no teams fit' : `only teams of ${fitting.join(' or ')} fit`
  return TEAM_SIZES.map((size) => {
    const disabled = !fitting.includes(size)
    return { size, disabled, reason: disabled ? `${mapName} seats ${seatsPhrase(map)} players, so ${fit}.` : null }
  })
}

export interface TeamPlayersOption {
  players: number
  teams: number
  /** "6 (2 teams)" (shown as a big "6" and a small "(2 teams)"). */
  label: string
  /** The player count, shown large. */
  primary: string
  /** "(2 teams)", shown small after it. */
  detail: string
  disabled: boolean
  reason: string | null
}

/**
 * The Players control for teams of `size`: every count that splits into
 * equal teams (at most the seat maximum), those the map can't seat or that
 * are below the lobby's seated floor disabled.
 */
export function teamPlayerOptions(map: SeatMap | null | undefined, size: TeamSize, floor = 0, mapName = 'This map'): TeamPlayersOption[] {
  const onMap = teamFormats(map)
  return teamFormats(null).filter((f) => f.size === size).map((f) => {
    const fits = onMap.some((m) => m.size === f.size && m.players === f.players)
    const seated = f.players < floor
    const reason = !fits ? `${mapName} seats ${seatsPhrase(map)} players.` : seated ? `${floor} seats are taken.` : null
    return { players: f.players, teams: f.teams, label: `${f.players} (${f.teams} teams)`, primary: String(f.players), detail: `(${f.teams} teams)`, disabled: !fits || seated, reason }
  })
}

/**
 * Keep a team format valid on a map: the size stays if the map has a format
 * for it (else the closest size that does, smaller first), and the count
 * snaps to the closest valid one, ties going down (teams-config.md
 * "Snapping"). Null when no team format fits the map.
 */
export function snapTeams(map: SeatMap | null | undefined, teams: TeamsConfig, players: number, floor = 0): { teams: TeamsConfig; players: number } | null {
  const sizes = [...TEAM_SIZES].sort((a, b) => Math.abs(a - teams.size) - Math.abs(b - teams.size) || a - b)
  for (const size of sizes) {
    // Never below a seated player: snapTeamSeats moves up to the next valid count.
    const count = snapTeamSeats(map, size, players, floor)
    if (count === null) continue
    return { teams: { ...teams, size }, players: count }
  }
  return null
}

/** The one-line notice after a map change snapped the format: "Hexcore seats up to 6: changed from 3v3v3 to 3v3." */
export function teamsSnapNotice(mapName: string, map: SeatMap | null | undefined, before: { size: number; players: number }, after: { size: number; players: number }): string {
  const range = mapSeatRange(map)
  const seats = range.min === range.max ? `seats ${range.max}` : `seats up to ${range.max}`
  return `${mapName} ${seats}: changed from ${teamFormatName(before.size, before.players)} to ${teamFormatName(after.size, after.players)}.`
}

/**
 * Who changes team when a Pick seats lobby is regrouped (teams-config.md
 * "Editing in the lobby"): seats are contiguous groups, so a new size moves
 * some seated players. Empty unless both the saved and the new format pick
 * seats.
 */
export function teamRegroupMoves(
  players: ReadonlyArray<Pick<GamePlayerResponse, 'displayName' | 'slot'>>,
  before: TeamsConfig | undefined,
  after: TeamsConfig | undefined,
): string[] {
  if (before?.assignment !== 'manual' || after?.assignment !== 'manual' || before.size === after.size) return []
  return [...players].sort((a, b) => a.slot - b.slot).flatMap((p) => {
    const from = teamForSlot(p.slot, before.size)
    const to = teamForSlot(p.slot, after.size)
    return from && to && from !== to ? [`${p.displayName} moves from ${teamLabel(from)} to ${teamLabel(to)}.`] : []
  })
}
